import React from 'react';
import { classes } from 'typestyle';

import { telemetryConsumerWrapper } from '~commonInfra/telemetryEvents/contexts/TelemetryConsumerWrapper';
import { ContentClickedTelemetryContext } from '~commonInfra/telemetryEvents/contexts/TelemetryContexts';
import { ContentClickedEventHandler } from '~commonInfra/telemetryEvents/types/ContentTelemetryTypes';
import { mouseUpFactory } from '~commonInfra/telemetryEvents/util/EventHandlerFactories';
import { SiteUrlsProps } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { DataSources } from '~src/datasource/resources/Datasources';
import { CardViewStyles } from '~src/spacesPlatform/components/card/CardViewStyles';
import { CardResizerService, CustomThumbnailTextTestId } from '~src/spacesPlatform/services/CardResizerService';

import { Messages } from '../../../codegen/Localize';
import { L10n } from '../../../commonInfra/L10n/L10n';
import { ExtractEncryptionMode } from '../../../commonInfra/utils/sites/SitePropertyTypes';
import { CommonDatasourceInfo } from '../../models/mappers/CommonDatasourceInfo';

export type DetailedDatasourceThumbnailOwnProps = Pick<React.AriaAttributes, 'aria-label'> & {
  readonly datasource: CommonDatasourceInfo;
  readonly contentUrl: string;
  readonly showExtractEncryptionStatus: boolean;
  readonly allowDragAndDrop: boolean;
  readonly hovered?: boolean;
  readonly selected?: boolean;
  readonly cardZoomPercentage: number;
}

export interface DetailedDatasourceThumbnailStateProps {
  readonly siteEARMode: ExtractEncryptionMode;
}

export type DetailedDatasourceThumbnailProps =
  SiteUrlsProps & DetailedDatasourceThumbnailOwnProps & DetailedDatasourceThumbnailStateProps;

export class DetailedDatasourceThumbnail extends React.Component<DetailedDatasourceThumbnailProps> {
  public render() {
    let containerClass = this.props.contentUrl ? CardViewStyles.CustomThumbnail : CardViewStyles.CustomThumbnailNoLinkPointer;
    const CardIcon = DataSources.getIconForDatasourceType(this.props.datasource, 'card');

    /* eslint-disable jsx-a11y/alt-text */
    return (
      <div className={classes(containerClass, {
        [CardViewStyles.SelectedClassName]: this.props.selected,
        [CardViewStyles.HoveredClassName]: this.props.hovered && !this.props.selected
      })} style={{ zoom: this.props.cardZoomPercentage }}>
        <div>
          <div className={CardViewStyles.CustomThumbnailTopContainer}>
            <div className={CardViewStyles.DatasourceCustomThumbnailImg}>
              <CardIcon size={60} data-tb-test-id={DataSources.isCertified(this.props.datasource) ? 'certification-icon' : ''} />
            </div>
            <div data-tb-test-id={CustomThumbnailTextTestId} style={{zoom: CardResizerService.instance.getCardZoomPercentageForCustomThumbailText()}}>
              {DataSources.DatasourceStatus(this.props.showExtractEncryptionStatus, this.props.datasource.connectionType, this.props.datasource.isFileBased, DataSources.isExtract(this.props.datasource), this.props.datasource.extractEncryptionState, this.props.datasource.extractCreationPending, this.props.siteEARMode)}
            </div>
          </div>
          <div data-tb-test-id={CustomThumbnailTextTestId} style={{zoom: CardResizerService.instance.getCardZoomPercentageForCustomThumbailText()}}>
            {this.lastRefreshTime()}
          </div>
        </div>
        {this.props.contentUrl &&
          telemetryConsumerWrapper(ContentClickedTelemetryContext.Consumer, (sendOpenContentTelemetry: ContentClickedEventHandler) =>
            /* eslint-disable jsx-a11y/anchor-has-content */
            <a
              onMouseUp={mouseUpFactory<HTMLAnchorElement>(sendOpenContentTelemetry)}
              href={this.props.contentUrl}
              className={classes(CardViewStyles.AnchorUnderlay, CardViewStyles.ThumbnailFocus)}
              /**
               * If card reordering allowed, disable link dragging.
               * Otherwise, set `draggable` explicitly to undefined rather than true.
               * (When `draggable` is true, the browser-provided drag avatar looks too similar
               * to the reorder card feature's avatar, which may confuse users.)
               */
              draggable={this.props.allowDragAndDrop ? false : undefined}
              aria-label={this.props['aria-label']}
            ></a>
          )
        }
      </div>
    );
  }

  private lastRefreshTime = (): JSX.Element => {
    const dateTime = this.props.datasource.lastRefreshedAt;
    return dateTime ? <span>{Messages.lastRefresh()} {L10n.localizedDateTime(dateTime)}</span> : <span>&nbsp;</span>;
  };
}
